import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Search,
  Person,
  DirectionsCar,
  AccessTime
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  rootContainer: {
    padding: 20,
    height: '100vh',
    overflow: 'hidden'
  },
  paper: {
    padding: 20,
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  searchField: {
    marginBottom: 20
  },
  chatsList: {
    flex: 1,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: 3,
    },
  },
  chatItem: {
    cursor: 'pointer',
    borderRadius: 8,
    marginBottom: 8,
    border: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      borderColor: colors.MAIN_COLOR,
    }
  },
  activeChatItem: {
    backgroundColor: '#e3f2fd',
    borderColor: colors.MAIN_COLOR,
  },
  unreadChip: {
    backgroundColor: '#f44336',
    color: '#fff',
    fontSize: '0.75rem',
    height: 20
  },
  userTypeChip: {
    fontSize: '0.7rem',
    height: 20
  },
  customerChip: {
    backgroundColor: '#4caf50',
    color: '#fff'
  },
  driverChip: {
    backgroundColor: '#ff9800',
    color: '#fff'
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  lastMessage: {
    color: '#666',
    fontSize: '0.9rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  timeText: {
    color: '#999',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200
  },
  emptyState: {
    textAlign: 'center',
    padding: 40,
    color: '#999'
  },
  createSampleButton: {
    marginTop: 16,
    backgroundColor: colors.MAIN_COLOR,
    color: '#fff',
    '&:hover': {
      backgroundColor: colors.MAIN_COLOR,
      opacity: 0.8
    }
  }
}));

export default function AdminSupportList() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [usersData, setUsersData] = useState({});

  const { fetchAllSupportChats, fetchUsersOnce } = api;
  const supportChats = useSelector(state => state.adminsupportdata.supportChats);
  const loading = useSelector(state => state.adminsupportdata.chatsLoading);
  const error = useSelector(state => state.adminsupportdata.chatsError);
  const users = useSelector(state => state.usersdata.users);

  // Fetch user data to display names
  useEffect(() => {
    if (api.fetchUsersOnce) {
      dispatch(fetchUsersOnce());
    }
  }, [dispatch, fetchUsersOnce]);

  // Process user data for quick lookup
  useEffect(() => {
    if (users && Array.isArray(users)) {
      const userData = {};
      users.forEach(user => {
        if (user.uid) {
          userData[user.uid] = {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            profile_image: user.profile_image || null,
            usertype: user.usertype || 'customer'
          };
        }
      });
      setUsersData(userData);
    }
  }, [users]);

  useEffect(() => {
    console.log('AdminSupportList: Fetching support chats...');
    dispatch(fetchAllSupportChats());
  }, [dispatch, fetchAllSupportChats]);

  useEffect(() => {
    console.log('AdminSupportList state:', {
      supportChats: supportChats.length,
      loading,
      error
    });
  }, [supportChats, loading, error]);

  // Show loading for a maximum of 8 seconds then show empty state
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        // Force stop loading after 8 seconds
        console.log('Forcing stop loading - no admin support data found');
      }
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [loading]);

  const handleChatSelect = (userId) => {
    setSelectedChatId(userId);
    navigate(`/admin-support-chat/${userId}`);
  };

  const formatTime = (timestamp) => {
    return moment(timestamp).format('HH:mm');
  };

  const formatDate = (timestamp) => {
    const messageDate = moment(timestamp);
    const today = moment();
    const yesterday = moment().subtract(1, 'day');

    if (messageDate.isSame(today, 'day')) {
      return formatTime(timestamp);
    } else if (messageDate.isSame(yesterday, 'day')) {
      return t('yesterday');
    } else {
      return messageDate.format('DD/MM');
    }
  };

  const getUserDisplayName = (userId, chatMessages) => {
    // Find the first user message (not admin message) to get consistent user name
    if (chatMessages && Array.isArray(chatMessages)) {
      const userMessage = chatMessages.find(msg => msg.fromType === 'user');
      if (userMessage && userMessage.userName) {
        return userMessage.userName;
      }
    }

    // If chatMessages is not an array, check if it's a single message object
    if (chatMessages && chatMessages.userName && chatMessages.fromType === 'user') {
      return chatMessages.userName;
    }

    // Fallback to users data
    const user = usersData[userId];
    if (user) {
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      return fullName || 'Unknown User';
    }
    return userId ? `${userId.slice(0, 8)}...` : 'Unknown User';
  };

  const getUserProfileImage = (userId, chatMessages) => {
    // Find the first user message (not admin message) to get consistent user image
    if (chatMessages && Array.isArray(chatMessages)) {
      const userMessage = chatMessages.find(msg => msg.fromType === 'user');
      if (userMessage && userMessage.userImage) {
        return userMessage.userImage;
      }
    }

    // If chatMessages is not an array, check if it's a single message object
    if (chatMessages && chatMessages.userImage && chatMessages.fromType === 'user') {
      return chatMessages.userImage;
    }

    // Fallback to users data
    const user = usersData[userId];
    return user?.profile_image || null;
  };

  const filteredChats = supportChats.filter(chat => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const userName = getUserDisplayName(chat.userId, chat.allMessages || [chat.lastMessage]).toLowerCase();
    return (
      userName.includes(searchLower) ||
      chat.userId.toLowerCase().includes(searchLower) ||
      chat.lastMessage.message.toLowerCase().includes(searchLower) ||
      chat.userType.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className={classes.rootContainer}>
        <Paper className={classes.paper}>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </Paper>
      </div>
    );
  }

  return (
    <div className={classes.rootContainer}>
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          {t('admin_support_list')}
        </Typography>

        <TextField
          className={classes.searchField}
          placeholder={t('search_chats')}
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {filteredChats.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6">
              {searchTerm ? t('no_search_results') : t('no_support_chats')}
            </Typography>
            {!searchTerm && (
              <>
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  Users will appear here when they start chatting with admin support from the mobile app.
                </Typography>
                {/* <Button
                  className={classes.createSampleButton}
                  onClick={createSampleData}
                  variant="contained"
                >
                  Create Sample Chat (for testing)
                </Button> */}
              </>
            )}
          </div>
        ) : (
          <List className={classes.chatsList}>
            {filteredChats.map((chat) => (
              <ListItem
                key={chat.userId}
                className={`${classes.chatItem} ${selectedChatId === chat.userId ? classes.activeChatItem : ''}`}
                onClick={() => handleChatSelect(chat.userId)}
              >
                <Avatar
                  sx={{ marginRight: 2 }}
                  src={getUserProfileImage(chat.userId, chat.allMessages || [chat.lastMessage])}
                >
                  {chat.userType === 'customer' ? <Person /> : <DirectionsCar />}
                </Avatar>

                <ListItemText
                  primary={
                    <div className={classes.chatHeader}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {getUserDisplayName(chat.userId, chat.allMessages || [chat.lastMessage])}
                        </Typography>
                        <Chip
                          size="small"
                          label={t(chat.userType)}
                          className={`${classes.userTypeChip} ${chat.userType === 'customer' ? classes.customerChip : classes.driverChip}`}
                        />
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        {chat.unreadCount > 0 && (
                          <Chip
                            size="small"
                            label={chat.unreadCount}
                            className={classes.unreadChip}
                          />
                        )}
                        <div className={classes.timeText}>
                          <AccessTime fontSize="inherit" />
                          {formatDate(chat.lastMessage.createdAt)}
                        </div>
                      </Box>
                    </div>
                  }
                  secondary={
                    <Typography className={classes.lastMessage}>
                      {chat.lastMessage.fromType === 'admin' ? t('you') + ': ' : ''}
                      {chat.lastMessage.message}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </div>
  );
} 